import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Printer, Boxes, UserPlus, FileSpreadsheet } from "lucide-react";
import camApi, { CAM_STATUSES, CAM_CONDITIONS, STATUS_META } from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";
import AssetDrawer, { PickerSelect } from "@/components/cam/AssetDrawer";
import { printLabels } from "@/components/cam/qr";
import BulkImportDialog from "@/components/cam/BulkImportDialog";

export default function Inventory() {
  const cam = useCam();
  const { categories, tree, catName, context } = cam;
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "", category_id: "", lab_id: "" });
  const [drawerId, setDrawerId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const canWrite = context?.cam_admin || context?.is_store;
  const [sel, setSel] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const query = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) query[k] = v; });
      const r = await camApi.assets(query);
      setAssets(r.assets || []);
      setSel([]);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.category_id, filters.lab_id]);

  // Deep-link from the scanner: /cam?asset=123 opens that asset's drawer.
  useEffect(() => {
    const id = params.get("asset");
    if (id) { setDrawerId(Number(id)); params.delete("asset"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => assets, [assets]);
  const allVisible = grouped.length > 0 && sel.length === grouped.length;
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSel(allVisible ? [] : grouped.map((a) => a.id));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search tag, name, serial, brand…" className="pl-9 h-10"
            value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <FilterSelect placeholder="All statuses" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
          options={[{ value: "", label: "All statuses" }, ...CAM_STATUSES.map((s) => ({ value: s, label: STATUS_META[s]?.label || s }))]} />
        <FilterSelect placeholder="All categories" value={filters.category_id} onChange={(v) => setFilters({ ...filters, category_id: v })}
          options={[{ value: "", label: "All categories" }, ...categories.map((c) => ({ value: String(c.id), label: c.name }))]} />
        <FilterSelect placeholder="All labs" value={filters.lab_id} onChange={(v) => setFilters({ ...filters, lab_id: v })}
          options={[{ value: "", label: "All labs" }, ...tree.labs.map((l) => ({ value: String(l.id), label: l.name }))]} />
        {canWrite && (
          <>
            <Button variant="outline" className="h-10" onClick={() => setImportOpen(true)}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />Import
            </Button>
            <Button className="h-10 bg-cobalt-500 hover:bg-cobalt-600" onClick={() => setFormOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />New asset
            </Button>
          </>
        )}
      </div>

      {canWrite && sel.length > 0 && (
        <div className="glass-strong rounded-2xl px-4 py-2.5 flex flex-wrap items-center gap-2 sticky top-2 z-10 border border-cobalt-500/30">
          <span className="text-sm font-medium">{sel.length} selected</span>
          <span className="h-4 w-px bg-black/10 dark:bg-white/15 mx-1" />
          <Button size="sm" className="bg-cobalt-500 hover:bg-cobalt-600" onClick={() => setBulkOpen(true)}>
            <UserPlus className="h-4 w-4 mr-1.5" />Assign selected
          </Button>
          <Button size="sm" variant="outline" onClick={() => printLabels(grouped.filter((a) => sel.includes(a.id)))}>
            <Printer className="h-4 w-4 mr-1.5" />Print labels
          </Button>
          <button onClick={() => setSel([])} className="text-sm text-muted-foreground hover:text-foreground ml-auto">Clear</button>
        </div>
      )}

      <div className="glass rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading assets…</div>
        ) : grouped.length === 0 ? (
          <div className="p-12 text-center">
            <Boxes className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p className="mt-3 text-sm text-muted-foreground">No assets match. {canWrite ? "Add your first asset to get started." : ""}</p>
          </div>
        ) : (
          <div className="overflow-x-auto touch-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                  {canWrite && <th className="px-3 py-3 w-9"><input type="checkbox" aria-label="Select all" checked={allVisible} onChange={toggleAll} className="h-4 w-4 align-middle accent-[#0654FC] cursor-pointer" /></th>}
                  <th className="px-4 py-3">Tag</th>
                  <th className="px-4 py-3">Asset</th>
                  <th className="px-4 py-3 hidden md:table-cell">Category</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 hidden sm:table-cell">Condition</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Warranty</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {grouped.map((a) => {
                  const m = STATUS_META[a.status] || {};
                  const warn = a.warranty_expiry && a.warranty_expiry < new Date().toISOString().slice(0, 10);
                  const checked = sel.includes(a.id);
                  return (
                    <tr key={a.id} className={`border-b last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] cursor-pointer ${checked ? "bg-cobalt-500/[0.06]" : ""}`} onClick={() => setDrawerId(a.id)}>
                      {canWrite && (
                        <td className="px-3 py-3 w-9" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" aria-label={`Select ${a.asset_tag}`} checked={checked} onChange={() => toggle(a.id)} className="h-4 w-4 align-middle accent-[#0654FC] cursor-pointer" />
                        </td>
                      )}
                      <td className="px-4 py-3 text-mono text-xs whitespace-nowrap">{a.asset_tag}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{a.name}</div>
                        {(a.brand || a.model) && <div className="text-xs text-muted-foreground">{[a.brand, a.model].filter(Boolean).join(" ")}</div>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{catName(a.category_id)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                          <span className="h-2 w-2 rounded-full" style={{ background: m.color || "#64748b" }} />{m.label || a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell capitalize text-muted-foreground">{a.condition}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {a.warranty_expiry ? <span className={warn ? "text-red-500" : "text-muted-foreground"}>{new Date(a.warranty_expiry).toLocaleDateString()}</span> : "—"}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button title="Print label" onClick={() => printLabels(a)} className="text-muted-foreground hover:text-cobalt-500 p-1"><Printer className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!loading && grouped.length > 0 && <div className="text-xs text-muted-foreground px-1">{grouped.length} asset{grouped.length === 1 ? "" : "s"}</div>}

      <AssetDrawer assetId={drawerId} open={!!drawerId} onOpenChange={(o) => !o && setDrawerId(null)} onChanged={load} />
      {formOpen && <AssetForm categories={categories} tree={tree} instituteCode={context?.institute_code} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
      {bulkOpen && <BulkAssignDialog ids={sel} tree={tree} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
      {importOpen && <BulkImportDialog onClose={() => setImportOpen(false)} onDone={load} />}
    </div>
  );
}

function FilterSelect({ placeholder, value, onChange, options }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger className="h-10 w-auto min-w-[140px] text-sm"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value || "__all"} value={o.value || "__all"}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function AssetForm({ categories, tree, onClose, onSaved }) {
  const blank = {
    name: "", category_id: "", asset_tag: "", brand: "", model: "", serial_number: "",
    condition: "good", status: "in_store", purchase_date: "", warranty_expiry: "", vendor: "", purchase_cost: "",
    owner_department_id: "", room_id: "", lab_id: "", subcategory_id: "", custom_fields: {},
  };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const cat = categories.find((c) => String(c.id) === String(f.category_id));
  const subs = cat?.subcategories || [];
  const visibleFields = (cat?.fields || []).filter((fld) => !fld.subcategory_id || String(fld.subcategory_id) === String(f.subcategory_id));

  const save = async () => {
    if (!f.name.trim() || !f.category_id) { toast.error("Name and category are required."); return; }
    setBusy(true);
    try {
      const payload = { ...f, category_id: Number(f.category_id), purchase_cost: f.purchase_cost === "" ? null : Number(f.purchase_cost) };
      ["owner_department_id", "room_id", "lab_id", "subcategory_id"].forEach((k) => { payload[k] = f[k] ? Number(f[k]) : null; });
      ["asset_tag", "purchase_date", "warranty_expiry"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      const r = await camApi.createAsset(payload);
      toast.success(`Asset ${r.asset?.asset_tag} created`);
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto touch-scroll">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">New asset</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label className="text-xs">Name *</Label><Input className="mt-1" value={f.name} onChange={set("name")} placeholder="e.g. Dell OptiPlex 7010" /></div>
          <div>
            <Label className="text-xs">Category *</Label>
            <PickerSelect placeholder="Select category" value={String(f.category_id)} onChange={(v) => setF((p) => ({ ...p, category_id: v, subcategory_id: "" }))} options={categories.map((c) => ({ value: String(c.id), label: c.name }))} className="mt-1" />
          </div>
          {subs.length > 0 && (
            <div>
              <Label className="text-xs">Sub-category</Label>
              <PickerSelect placeholder="Select sub-category" value={String(f.subcategory_id)} onChange={set("subcategory_id")} options={subs.map((s) => ({ value: String(s.id), label: s.name }))} className="mt-1" />
            </div>
          )}
          <div><Label className="text-xs">Asset tag</Label><Input className="mt-1 text-mono text-xs" value={f.asset_tag} onChange={set("asset_tag")} placeholder="Auto-generated if blank" /></div>
          <div><Label className="text-xs">Brand</Label><Input className="mt-1" value={f.brand} onChange={set("brand")} /></div>
          <div><Label className="text-xs">Model</Label><Input className="mt-1" value={f.model} onChange={set("model")} /></div>
          <div><Label className="text-xs">Serial number</Label><Input className="mt-1 text-mono text-xs" value={f.serial_number} onChange={set("serial_number")} /></div>
          <div>
            <Label className="text-xs">Condition</Label>
            <PickerSelect placeholder="Condition" value={f.condition} onChange={set("condition")} options={CAM_CONDITIONS.map((c) => ({ value: c, label: c }))} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Room</Label>
            <PickerSelect placeholder="Room" value={String(f.room_id)} onChange={set("room_id")} options={tree.rooms.map((r) => ({ value: String(r.id), label: r.name }))} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Lab</Label>
            <PickerSelect placeholder="Lab" value={String(f.lab_id)} onChange={set("lab_id")} options={tree.labs.map((l) => ({ value: String(l.id), label: l.name }))} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Owner department</Label>
            <PickerSelect placeholder="Department" value={String(f.owner_department_id)} onChange={set("owner_department_id")} options={tree.departments.map((d) => ({ value: String(d.id), label: d.name }))} className="mt-1" />
          </div>
          <div><Label className="text-xs">Vendor</Label><Input className="mt-1" value={f.vendor} onChange={set("vendor")} /></div>
          <div><Label className="text-xs">Purchase date</Label><Input type="date" className="mt-1" value={f.purchase_date} onChange={set("purchase_date")} /></div>
          <div><Label className="text-xs">Purchase cost (₹)</Label><Input type="number" className="mt-1" value={f.purchase_cost} onChange={set("purchase_cost")} /></div>
          <div><Label className="text-xs">Warranty expiry</Label><Input type="date" className="mt-1" value={f.warranty_expiry} onChange={set("warranty_expiry")} /></div>
        </div>

        {visibleFields.length > 0 && (
          <div className="mt-1">
            <div className="overline mb-2">{cat.name} specifications</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {visibleFields.map((fld) => (
                <div key={fld.key}>
                  <Label className="text-xs">{fld.label}{fld.is_required ? " *" : ""}</Label>
                  <CustomFieldInput fld={fld} value={f.custom_fields?.[fld.key]} onChange={(v) => setF((p) => ({ ...p, custom_fields: { ...p.custom_fields, [fld.key]: v } }))} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={save} disabled={busy}>{busy ? "Saving…" : "Create asset"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CustomFieldInput({ fld, value, onChange }) {
  if (fld.data_type === "boolean")
    return <PickerSelect placeholder="—" value={value === true ? "true" : value === false ? "false" : ""} onChange={(v) => onChange(v === "true")} options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]} className="mt-1" />;
  if (fld.data_type === "select")
    return <PickerSelect placeholder="Select" value={value || ""} onChange={onChange} options={(fld.options || []).map((o) => ({ value: String(o), label: String(o) }))} className="mt-1" />;
  const type = fld.data_type === "number" ? "number" : fld.data_type === "date" ? "date" : "text";
  return <Input type={type} className="mt-1" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

// Bulk-assign the selected assets to a person, a lab, or a room in one shot.
function BulkAssignDialog({ ids, tree, onClose, onDone }) {
  const [mode, setMode] = useState("person");
  const [email, setEmail] = useState("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [labId, setLabId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const body = { asset_ids: ids };
    if (mode === "person") {
      if (!email.trim()) { toast.error("Enter the person's email."); return; }
      body.email = email.trim();
      if (employeeCode.trim()) body.employee_code = employeeCode.trim();
    } else if (mode === "lab") {
      if (!labId) { toast.error("Pick a lab."); return; }
      body.lab_id = Number(labId);
    } else {
      if (!roomId) { toast.error("Pick a room."); return; }
      body.room_id = Number(roomId);
    }
    setBusy(true);
    try {
      const r = await camApi.bulkAssign(body);
      toast.success(`${r.assigned} asset${r.assigned === 1 ? "" : "s"} assigned${r.skipped ? ` · ${r.skipped} skipped` : ""}`);
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const Seg = ({ id, label }) => (
    <button onClick={() => setMode(id)}
      className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${mode === id ? "bg-cobalt-500 text-white shadow-sm" : "hover:bg-black/5 text-muted-foreground"}`}>
      {label}
    </button>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">Assign {ids.length} asset{ids.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
        <div className="flex gap-1 p-1 rounded-xl bg-black/5 dark:bg-white/5">
          <Seg id="person" label="To a person" />
          <Seg id="lab" label="To a lab" />
          <Seg id="room" label="To a room" />
        </div>

        <div className="space-y-3 pt-1">
          {mode === "person" && (
            <>
              <div><Label className="text-xs">Person's email</Label><Input className="mt-1" placeholder="name@institute.edu" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              <div><Label className="text-xs">Employee code (optional)</Label><Input className="mt-1" value={employeeCode} onChange={(e) => setEmployeeCode(e.target.value)} /></div>
            </>
          )}
          {mode === "lab" && (
            <div><Label className="text-xs">Lab</Label><PickerSelect placeholder="Select lab" value={labId} onChange={setLabId} options={tree.labs.map((l) => ({ value: String(l.id), label: l.name }))} className="mt-1" /></div>
          )}
          {mode === "room" && (
            <div><Label className="text-xs">Room</Label><PickerSelect placeholder="Select room" value={roomId} onChange={setRoomId} options={tree.rooms.map((r) => ({ value: String(r.id), label: r.name }))} className="mt-1" /></div>
          )}
          <p className="text-[11px] text-muted-foreground">
            {mode === "person" ? "Each asset is marked Assigned and added to this person's My Assets." : "Each asset is moved to this location and marked Installed."}
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={submit} disabled={busy}>{busy ? "Assigning…" : "Assign"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
